import { api } from "@/shared/api/baseApi";
import { useCurrentUser } from "@/shared/auth/useCurrentUser";
import { useHasPermission } from "@/shared/auth/usePermissions";
import { useAppDispatch } from "@/shared/hooks/redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Permission } from "@keystone/contracts/shared";
import {
  ActionIcon,
  AppShell,
  Avatar,
  Box,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { FiBriefcase, FiHome, FiKey, FiLogOut, FiUsers } from "react-icons/fi";
import { Outlet, NavLink as RouterNavLink, useLocation, useNavigate } from "react-router";

export function AuthenticatedLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { t } = useTranslation("common");
  const { logout } = useAuth0();
  const user = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLogout = () => {
    close();
    dispatch(api.util.resetApiState());
    void logout({ logoutParams: { returnTo: window.location.origin } });
    navigate("/login");
  };

  const canManageOrganizations = useHasPermission(Permission.ManageOrganizations);
  const canManageUsers = useHasPermission(Permission.ManageUsers);
  const canRequestTempAccess = useHasPermission(Permission.CreateTempAccessRequests);

  const userLabel = user?.name ?? user?.email ?? t("user.anonymous");

  const navItems = [
    { to: "/", label: t("nav.dashboard"), icon: <FiHome aria-hidden="true" />, show: true },
    {
      to: "/organizations",
      label: t("nav.organizations"),
      icon: <FiBriefcase aria-hidden="true" />,
      show: canManageOrganizations,
    },
    {
      to: "/users",
      label: t("nav.users"),
      icon: <FiUsers aria-hidden="true" />,
      show: canManageUsers,
    },
    {
      to: "/temp-access",
      label: t("nav.tempAccess"),
      icon: <FiKey aria-hidden="true" />,
      show: canRequestTempAccess,
    },
  ].filter((item) => item.show);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <img src="/favicon.svg" alt="" height={32} />
            <Text
              component="h1"
              fw={700}
              size="lg"
              variant="gradient"
              gradient={{ from: "blue.8", to: "cyan.5" }}
            >
              {t("app.name")}
            </Text>
          </Group>
          <Burger
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
            aria-label={t("nav.dashboard")}
          />
          <Box visibleFrom="sm">
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  radius="xl"
                  size="lg"
                  aria-label={t("user.menu")}
                  title={userLabel}
                >
                  <Avatar radius="xl" size="md" color="initials" name={userLabel} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<FiLogOut aria-hidden="true" />} onClick={handleLogout}>
                  {t("actions.logout")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Box>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={4}>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                component={RouterNavLink}
                to={item.to}
                label={item.label}
                leftSection={item.icon}
                active={
                  item.to === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.to)
                }
                onClick={close}
              />
            ))}
          </Stack>
        </AppShell.Section>
        <AppShell.Section hiddenFrom="sm">
          <Divider my="xs" />
          <Stack gap={4}>
            <Group gap="sm" px="sm" py="xs" wrap="nowrap">
              <Avatar radius="xl" size="sm" color="initials" name={userLabel} />
              <Text size="sm" truncate>
                {userLabel}
              </Text>
            </Group>
            <NavLink
              label={t("actions.logout")}
              leftSection={<FiLogOut aria-hidden="true" />}
              onClick={handleLogout}
            />
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
